import React, { useState } from 'react';
import { Loader2, Upload, X, Film } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const labelCls = 'text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block';

/**
 * Tidy hero media controls for a rig showcase page:
 *  - HERO PHOTO: pick from the shared gallery thumbnails (click to select),
 *    with a manual URL / upload fallback and a preview + remove.
 *  - HERO VIDEO: upload an .mp4 (or paste a URL) that autoplays muted as the
 *    page background, with a preview + remove.
 */
export default function RigHeroMedia({ photos = [], heroImageUrl, onHeroImageUrl, heroVideoUrl, onHeroVideoUrl }) {
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingVid, setUploadingVid] = useState(false);

  const galleryUrls = (photos || [])
    .map((p) => (typeof p === 'string' ? p : p.url))
    .filter(Boolean);

  const uploadHeroImage = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      onHeroImageUrl?.(file_url);
    } finally {
      setUploadingImg(false);
    }
  };

  const uploadHeroVideo = async (file) => {
    if (!file) return;
    setUploadingVid(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      onHeroVideoUrl?.(file_url);
    } finally {
      setUploadingVid(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* HERO PHOTO */}
      <div>
        <Label className={labelCls}>HERO PHOTO</Label>
        {galleryUrls.length ? (
          <>
            <p className="text-white/30 text-[10px] mb-2">Click a gallery photo to use it as the hero image.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {galleryUrls.map((url, i) => {
                const isHero = url === heroImageUrl;
                return (
                  <button key={i} type="button" onClick={() => onHeroImageUrl?.(url)}
                    className={`relative w-20 h-14 overflow-hidden border-2 transition-colors ${isHero ? 'border-primary' : 'border-white/10 hover:border-white/30'}`}>
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    {isHero && <span className="absolute top-0.5 left-0.5 bg-primary text-white text-[7px] font-bold tracking-widest px-1 py-0.5">HERO</span>}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <p className="text-white/30 text-[10px] mb-3">Add photos to the gallery below, then pick one as the hero.</p>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={heroImageUrl || ''} onChange={(e) => onHeroImageUrl?.(e.target.value)} placeholder="Or paste an image URL" className="bg-[#111115] border-white/10 text-white h-10 placeholder:text-white/20 focus:border-primary/60" />
          <label className="flex items-center gap-1.5 text-xs font-bold tracking-wider cursor-pointer text-white/50 hover:text-white border border-white/10 px-3 h-10 justify-center whitespace-nowrap">
            {uploadingImg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploadingImg ? 'UPLOADING' : 'UPLOAD'}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadHeroImage(e.target.files?.[0]); e.target.value = ''; }} disabled={uploadingImg} />
          </label>
        </div>
        {heroImageUrl && (
          <div className="mt-2 flex items-center gap-2">
            <img src={heroImageUrl} alt="Hero" className="w-24 h-16 object-cover border border-white/10" />
            <button type="button" onClick={() => onHeroImageUrl?.('')} className="flex items-center gap-1 text-xs font-bold tracking-wider text-red-400/80 hover:text-red-400">
              <X className="w-3.5 h-3.5" /> REMOVE
            </button>
          </div>
        )}
      </div>

      {/* HERO VIDEO */}
      <div>
        <Label className={labelCls}>HERO VIDEO (MP4)</Label>
        <p className="text-white/30 text-[10px] mb-2">Autoplays muted as the page background. Upload an .mp4 or paste a URL.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={heroVideoUrl || ''} onChange={(e) => onHeroVideoUrl?.(e.target.value)} placeholder="https://.../video.mp4" className="bg-[#111115] border-white/10 text-white h-10 placeholder:text-white/20 focus:border-primary/60" />
          <label className="flex items-center gap-1.5 text-xs font-bold tracking-wider cursor-pointer text-white/50 hover:text-white border border-white/10 px-3 h-10 justify-center whitespace-nowrap">
            {uploadingVid ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}
            {uploadingVid ? 'UPLOADING' : 'UPLOAD .MP4'}
            <input type="file" accept="video/mp4,video/*" className="hidden" onChange={(e) => { uploadHeroVideo(e.target.files?.[0]); e.target.value = ''; }} disabled={uploadingVid} />
          </label>
        </div>
        {heroVideoUrl && (
          <div className="mt-2 flex items-center gap-2">
            <video src={heroVideoUrl} className="w-24 h-16 object-cover border border-white/10 bg-black" muted />
            <button type="button" onClick={() => onHeroVideoUrl?.('')} className="flex items-center gap-1 text-xs font-bold tracking-wider text-red-400/80 hover:text-red-400">
              <X className="w-3.5 h-3.5" /> REMOVE
            </button>
          </div>
        )}
      </div>
    </div>
  );
}